import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Timer, Zap, ShieldCheck, LineChart, Download, Loader2, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useLang } from "@/i18n";

const HERO = "https://images.unsplash.com/photo-1552072092-7f9b8d63efcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxib3hpbmclMjBmaWdodGVyJTIwcmluZyUyMHRyYWluaW5nfGVufDB8fHx8MTc5MDMzMjc5MXww&ixlib=rb-4.1.0&q=85";

export default function BlueprintSales() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [price, setPrice] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [token, setToken] = useState("");
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoMsg, setPromoMsg] = useState("");
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverMsg, setRecoverMsg] = useState("");

  useEffect(() => {
    api.get("/product/blueprint").then((r) => setPrice(r.data.price));
  }, []);

  useEffect(() => {
    const urlToken = params.get("token");
    if (urlToken) {
      api
        .get("/blueprint/verify", { params: { token: urlToken } })
        .then(() => {
          localStorage.setItem("guardia_bp_token", urlToken);
          const prog = params.get("program");
          navigate(`/blueprint/console${prog ? `?program=${encodeURIComponent(prog)}` : ""}`);
        })
        .catch(() => toast.error("Invalid access link"));
    }
  }, [params, navigate]);

  const features = lang === "it"
    ? [
        { icon: Timer, title: "Round Timer con Campana", desc: "Console rounds con segnale acustico, durata (2m/3m) e recupero personalizzabili, prompt combo in tempo reale." },
        { icon: Zap, title: "Generatore di Combinazioni", desc: "Drill dinamico con selettore difficoltà: dal jab-cross ai combo kickboxing a 5 colpi con calci e ganci al fegato." },
        { icon: ShieldCheck, title: "Checklist Tecnica Interattiva", desc: "Scorecard di padronanza: valuti guardia, chin tuck, torsione dell'anca e pivot. Punteggio forma calcolato." },
        { icon: LineChart, title: "Log & Motore di Progressione", desc: "Tracker sessioni: round completati, RPE, note e conteggio colpi salvati sul tuo account." },
        { icon: Download, title: "Field Log Card", desc: "Storico delle sessioni sempre disponibile per rivedere i progressi settimana dopo settimana." },
      ]
    : [
        { icon: Timer, title: "Round Timer with Bell", desc: "Rounds console with acoustic bell, customizable duration (2m/3m) and rest, real-time combo prompts." },
        { icon: Zap, title: "Combination Generator", desc: "Dynamic drill with difficulty selector: from jab-cross to 5-hit kickboxing combos with kicks and liver hooks." },
        { icon: ShieldCheck, title: "Interactive Technical Checklist", desc: "Mastery scorecard: rate guard, chin tuck, hip torque and pivot. Computed form score." },
        { icon: LineChart, title: "Log & Progression Engine", desc: "Session tracker: completed rounds, RPE, notes and strike counts saved to your account." },
        { icon: Download, title: "Field Log Card", desc: "Session history always available to review your progress week after week." },
      ];

  const buy = async () => {
    if (!email) return toast.error(lang === "it" ? "Inserisci la tua email" : "Enter your email");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        email,
        origin_url: window.location.origin,
        coupon_code: promoInfo ? promo.trim().toUpperCase() : null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout error");
      setLoading(false);
    }
  };

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: promo.trim() });
      if (data.valid) {
        setPromoInfo(data);
        setPromoMsg(t("promo_applied"));
      } else {
        setPromoInfo(null);
        setPromoMsg(t("promo_invalid"));
      }
    } catch (e) {
      setPromoInfo(null);
      setPromoMsg(t("promo_invalid"));
    }
  };

  const discounted = () => {
    const base = price ? price.amount : 39;
    if (!promoInfo) return null;
    if (promoInfo.percent_off) return base * (1 - promoInfo.percent_off / 100);
    if (promoInfo.amount_off) return Math.max(0, base - promoInfo.amount_off);
    return null;
  };
  const finalPrice = discounted();

  const verifyAccess = async () => {
    try {
      await api.get("/blueprint/verify", { params: { token } });
      localStorage.setItem("guardia_bp_token", token);
      navigate("/blueprint/console");
    } catch (e) {
      toast.error(lang === "it" ? "Token non valido" : "Invalid token");
    }
  };

  const recover = async () => {
    if (!recoverEmail.trim()) return;
    try {
      await api.post("/blueprint/recover", { email: recoverEmail.trim() });
    } catch (e) {
      /* generic response regardless */
    }
    setRecoverMsg(t("recover_sent"));
    setRecoverEmail("");
  };

  return (
    <div>
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0c0d10] via-[#0c0d10]/90 to-[#0c0d10]/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c0d10] to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="text-xs font-mono uppercase tracking-[0.25em] text-[#F59E0B]">{t("bp_eyebrow")}</span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-white leading-none">
              {t("bp_title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-xl">{t("bp_sub")}</p>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-mono text-4xl font-bold text-white">
                {price ? `€${price.amount.toFixed(0)}` : "€39"}
              </span>
              <span className="text-sm text-zinc-500">/ one-time</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-3 gap-10">
        {/* features */}
        <div className="lg:col-span-2">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold uppercase text-white">{t("bp_features")}</h2>
          <div className="mt-6 space-y-4">
            {features.map((f, i) => (
              <div key={i} className="flex gap-4 bg-[#14161b] border border-[#272b35] rounded-lg p-5 fade-up">
                <span className="w-11 h-11 shrink-0 grid place-items-center bg-[#E63946]/15 rounded-lg">
                  <f.icon size={22} className="text-[#E63946]" />
                </span>
                <div>
                  <h3 className="font-heading text-xl font-bold uppercase text-white">{f.title}</h3>
                  <p className="mt-1 text-sm text-zinc-400">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* checkout card */}
        <div>
          <div className="sticky top-24 bg-[#14161b] border border-[#272b35] rounded-xl p-6">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-3xl font-bold text-white">{price ? `€${price.amount.toFixed(0)}` : "€39"}</span>
              <span className="text-xs text-zinc-500">/ one-time</span>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-zinc-300">
              {(lang === "it"
                ? ["Accesso illimitato alla console", "Aggiornamenti inclusi", "Log salvato sul tuo account"]
                : ["Unlimited console access", "Updates included", "Log saved to your account"]
              ).map((x, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check size={16} className="text-[#10B981]" /> {x}
                </li>
              ))}
            </ul>

            <label className="block mt-6 text-xs font-mono uppercase tracking-widest text-zinc-500">
              {t("bp_email_label")}
            </label>
            <input
              type="email"
              value={email}
              data-testid="blueprint-email-input"
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="mt-2 w-full bg-[#0c0d10] border border-[#272b35] rounded-md px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E63946]"
            />

            <label className="block mt-4 text-xs font-mono uppercase tracking-widest text-zinc-500">{t("promo_label")}</label>
            <div className="mt-2 flex gap-2">
              <input
                value={promo}
                data-testid="promo-code-input"
                onChange={(e) => { setPromo(e.target.value); setPromoInfo(null); setPromoMsg(""); }}
                placeholder="RING20"
                className="flex-1 bg-[#0c0d10] border border-[#272b35] rounded-md px-4 py-2.5 text-sm text-white uppercase placeholder:text-zinc-600 focus:outline-none focus:border-[#E63946]"
              />
              <button
                onClick={applyPromo}
                data-testid="apply-promo-btn"
                className="shrink-0 px-4 py-2.5 border border-[#3a3f4b] hover:border-white text-white text-xs font-bold uppercase rounded-md"
              >
                {t("promo_apply")}
              </button>
            </div>
            {promoMsg && (
              <p data-testid="promo-message" className={`mt-2 text-xs font-semibold ${promoInfo ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                {promoMsg}
              </p>
            )}
            {finalPrice !== null && (
              <div data-testid="discounted-price" className="mt-3 flex items-baseline gap-2">
                <span className="text-sm text-zinc-500 line-through">€{(price ? price.amount : 39).toFixed(0)}</span>
                <span className="font-mono text-2xl font-bold text-[#10B981]">€{finalPrice.toFixed(2)}</span>
              </div>
            )}

            <button
              onClick={buy}
              disabled={loading}
              data-testid="checkout-blueprint-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase tracking-wide rounded-md transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {t("bp_buy")}
            </button>

            <button
              onClick={() => setShowAccess((v) => !v)}
              data-testid="have-access-toggle"
              className="mt-3 w-full text-xs text-zinc-500 hover:text-zinc-300"
            >
              {t("bp_have_access")}
            </button>
            {showAccess && (
              <div className="mt-3 space-y-2">
                <input
                  value={token}
                  data-testid="access-token-input"
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="access token"
                  className="w-full bg-[#0c0d10] border border-[#272b35] rounded-md px-3 py-2 text-xs text-white focus:outline-none focus:border-[#E63946]"
                />
                <button
                  onClick={verifyAccess}
                  data-testid="verify-access-btn"
                  className="w-full px-4 py-2 border border-[#3a3f4b] hover:border-white text-white text-xs font-bold uppercase rounded-md"
                >
                  {t("verify_token")}
                </button>

                <div className="pt-3 mt-1 border-t border-[#272b35]">
                  <p className="text-[11px] text-zinc-500 mb-2">{t("recover_title")}</p>
                  {recoverMsg ? (
                    <p data-testid="recover-message" className="text-xs text-[#10B981] font-semibold">{recoverMsg}</p>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="email"
                        value={recoverEmail}
                        data-testid="recover-email-input"
                        onChange={(e) => setRecoverEmail(e.target.value)}
                        placeholder={t("recover_email_ph")}
                        className="w-full bg-[#0c0d10] border border-[#272b35] rounded-md px-3 py-2 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E63946]"
                      />
                      <button
                        onClick={recover}
                        data-testid="recover-access-btn"
                        className="w-full px-4 py-2 bg-[#14161b] border border-[#3a3f4b] hover:border-[#E63946] text-white text-xs font-bold uppercase rounded-md"
                      >
                        {t("recover_btn")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            <p className="mt-4 text-[11px] text-zinc-600 text-center flex items-center justify-center gap-1.5">
              <Lock size={11} /> {lang === "it" ? "Pagamento sicuro con Stripe" : "Secure payment with Stripe"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
